import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { resolveDisplayImageUrl } from '../../shared/utils/mediaPreview';
import './LazyImage.css';

// Constants
const INTERSECTION_ROOT_MARGIN = '50px';
const INTERSECTION_THRESHOLD = 0.01;
const DEFAULT_PLACEHOLDER = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 300"%3E%3Crect width="400" height="300" fill="%23f3f4f6"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="Arial, sans-serif" font-size="18" fill="%239ca3af"%3ELoading...%3C/text%3E%3C/svg%3E';

// Image cache to prevent re-loading and handle race conditions
// Structure: Map<url, { loaded: boolean, displayUrl?: string, promise?: Promise }>
const imageCache = new Map();

/**
 * LazyImage component that loads images only when they enter the viewport.
 * Features:
 * - Intersection Observer for lazy loading
 * - Image caching to prevent duplicate loads
 * - Race condition prevention
 * - HEIC/HEIF remote URL conversion for browser display
 * - Error handling with fallback (never throws uncaught errors)
 */
const LazyImage = ({
  src,
  alt = '',
  className = '',
  placeholder = DEFAULT_PLACEHOLDER,
  priority = false,
  onError,
  ...props
}) => {
  const cacheEntry = imageCache.get(src);
  const [imageSrc, setImageSrc] = useState(() =>
    cacheEntry?.loaded ? (cacheEntry.displayUrl || src) : placeholder
  );
  const [imageRef, setImageRef] = useState(null);
  const [isLoaded, setIsLoaded] = useState(() => cacheEntry?.loaded || false);
  const [hasError, setHasError] = useState(false);

  const observerRef = useRef(null);
  const loadingRef = useRef(false);
  const mountedRef = useRef(true);

  const updateImageState = useCallback((url) => {
    if (mountedRef.current) {
      setImageSrc(url);
      setIsLoaded(true);
      setHasError(false);
    }
  }, []);

  const handleError = useCallback((e) => {
    if (!mountedRef.current) return;
    setHasError(true);
    setImageSrc(placeholder);
    onError?.(e);
  }, [onError, placeholder]);

  const loadImage = useCallback(async (imageUrl) => {
    const cached = imageCache.get(imageUrl);

    if (cached?.loaded) {
      updateImageState(cached.displayUrl || imageUrl);
      return;
    }

    if (cached?.promise) {
      await cached.promise;
      const entry = imageCache.get(imageUrl);
      if (mountedRef.current && entry?.loaded) {
        updateImageState(entry.displayUrl || imageUrl);
      }
      return;
    }

    const loadPromise = (async () => {
      try {
        const displayUrl = await resolveDisplayImageUrl(imageUrl);

        await new Promise((resolve) => {
          const img = new Image();

          img.onload = () => {
            const applyLoaded = () => {
              imageCache.set(imageUrl, { loaded: true, displayUrl });
              updateImageState(displayUrl);
              resolve();
            };

            if (typeof img.decode === 'function') {
              img.decode().then(applyLoaded).catch(applyLoaded);
            } else {
              applyLoaded();
            }
          };

          img.onerror = () => {
            imageCache.delete(imageUrl);
            if (mountedRef.current) {
              setHasError(true);
              setImageSrc(placeholder);
              onError?.({ target: img });
            }
            resolve();
          };

          img.src = displayUrl;
        });
      } catch {
        imageCache.delete(imageUrl);
        if (mountedRef.current) {
          setHasError(true);
          setImageSrc(placeholder);
        }
      }
    })();

    imageCache.set(imageUrl, { loaded: false, promise: loadPromise });
    await loadPromise;
  }, [onError, placeholder, updateImageState]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!priority || !src || loadingRef.current) return;
    const cached = imageCache.get(src);
    if (cached?.loaded) {
      updateImageState(cached.displayUrl || src);
      return;
    }
    loadingRef.current = true;
    loadImage(src).finally(() => {
      if (mountedRef.current) loadingRef.current = false;
    });
  }, [priority, src, loadImage, updateImageState]);

  useEffect(() => {
    if (priority || !imageRef || !src || loadingRef.current) return;

    const cached = imageCache.get(src);
    if (cached?.loaded) {
      updateImageState(cached.displayUrl || src);
      return;
    }

    const observerOptions = {
      root: null,
      rootMargin: INTERSECTION_ROOT_MARGIN,
      threshold: INTERSECTION_THRESHOLD,
    };

    const handleIntersection = (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting || loadingRef.current || !mountedRef.current) {
          return;
        }

        loadingRef.current = true;

        const cachedCheck = imageCache.get(src);
        if (cachedCheck?.loaded) {
          updateImageState(cachedCheck.displayUrl || src);
          loadingRef.current = false;
          observerRef.current?.unobserve(imageRef);
          return;
        }

        loadImage(src).finally(() => {
          if (mountedRef.current) {
            loadingRef.current = false;
          }
        });

        observerRef.current?.unobserve(imageRef);
      });
    };

    const observer = new IntersectionObserver(handleIntersection, observerOptions);
    observerRef.current = observer;
    observer.observe(imageRef);

    return () => {
      if (observer && imageRef) {
        observer.unobserve(imageRef);
      }
    };
  }, [imageRef, src, loadImage, updateImageState, priority]);

  const imageClassName = [
    'lazy-image',
    className,
    isLoaded && 'lazy-image-loaded',
    hasError && 'lazy-image-error',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <img
      ref={setImageRef}
      src={imageSrc}
      alt={alt}
      className={imageClassName}
      onError={handleError}
      loading="lazy"
      {...props}
    />
  );
};

LazyImage.propTypes = {
  src: PropTypes.string.isRequired,
  alt: PropTypes.string,
  className: PropTypes.string,
  placeholder: PropTypes.string,
  priority: PropTypes.bool,
  onError: PropTypes.func,
};

export default LazyImage;
